import { EcomError, EcomService } from "~/services/ecom.service";
import { WoosmapService } from "~/services/woosmap.service";
import type {
  Branch,
  BranchGooglePlaceData,
  Geoposition,
  NearestBranchRequest,
} from "~/types/ecom/branch.type";
import type { WoosmapLocalitySuggestion } from "~/types/woosmap/locality.type";
import EcomCustomerService from "~/services/ecom/customer.service";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

type BranchStoreState = {
  ecomService: EcomService | null;
  woosmapService: WoosmapService | null;
  localitySearchInput: string;

  selectedLocality: WoosmapLocalitySuggestion | null;
  autoCompleteSuggestions: WoosmapLocalitySuggestion[];
  autoCompleteDropdownVisible: boolean;

  targetBranch: Branch | null;
  allBranches: Branch[];
  allBranchNames: string[];
  nearestBranches: Branch[];
  categorizedAllBranches: Record<string, Branch[]> | null;
  branchGoogleFeedback: Pick<BranchGooglePlaceData, 'rating' | 'review_count'> | null,
  branchGooglePlaceId: string,

  currentBranchChoice: Branch | null; // for radio button
  defaultBranch: Branch | null;
  lastSavedBranch: Branch | null;
  currentSavedBranch: Branch | null;

  geolocationError: unknown;
  currentLocation: Geoposition | null;
  selectedLetter: string;
  branchNotFoundForLocality: any;
  branchNotFoundForPostcode: string;
  searchTimer: ReturnType<typeof setTimeout> | null;

  isProcessingLocalitySearch: boolean;
  isProcessingNearestBranchSearch: boolean;
  isDefaultBranchSelected: boolean;
  isAutoCompleteSearchEnabled: boolean;
  locationPermissionDenied: boolean;

  noNearestBranchAvailable: boolean;
  noSuggestionsAvailable: boolean;

  branchSelectorModalVisible: boolean;
  debugValues: {
    currentLat: number;
    currentLong: number;
    radius?: number;
  } | null;
};

export const useBranchStore = defineStore("branchStore", {
  state: (): BranchStoreState => ({
    ecomService: null,
    woosmapService: null,

    localitySearchInput: "",
    selectedLocality: null,
    autoCompleteSuggestions: [],
    autoCompleteDropdownVisible: false,
    nearestBranches: [],
    categorizedAllBranches: null,

    branchGoogleFeedback: null,
    branchGooglePlaceId: '',

    currentBranchChoice: null,
    defaultBranch: null,
    lastSavedBranch: null,
    currentSavedBranch: null,
    geolocationError: null,
    currentLocation: null,
    branchNotFoundForLocality: null,
    branchNotFoundForPostcode: "",
    selectedLetter: "All",

    isProcessingLocalitySearch: false,
    isProcessingNearestBranchSearch: false,

    isDefaultBranchSelected: false,
    isAutoCompleteSearchEnabled: true,
    locationPermissionDenied: false,

    noNearestBranchAvailable: false,
    noSuggestionsAvailable: false,
    branchSelectorModalVisible: false,
    searchTimer: null,
    targetBranch: null,
    allBranchNames: [],
    allBranches: [],
    debugValues: null,
  }),
  persist: [
    {
      storage: import.meta.client ? localStorage : undefined,
      pick: ["defaultBranch", "lastSavedBranch"],
    },
    {
      pick: ["currentSavedBranch.id", "lastSavedBranch.id", "lastSavedBranch.name"]
    }
  ],
  getters: {
    is_branch_set: (state): boolean => !!state.lastSavedBranch?.id,
    formattedSearchQuery: (state) =>
      state.localitySearchInput.replace('"', '\\"').replace(/^\s+|\s+$/g, ""),
    mapZoomCenter: (state) => {
      if (state.noNearestBranchAvailable || !state.nearestBranches.length)
        return null;
      return {
        latitude: Number(state.nearestBranches[0].geolocation.latitude),
        longitude: Number(state.nearestBranches[0].geolocation.longitude),
      };
    },
    locationBlockMessage: (state) => {
      if (
        state.locationPermissionDenied &&
        !state.isProcessingNearestBranchSearch
      )
        return useTranslation(
          "currentLocationMessage",
          "Toegang tot huidige locatie geblokkeerd"
        );
    },
    noResultState: (state) => {
      return (
        !state.nearestBranches.length && !state.isProcessingNearestBranchSearch
      );
    },
    openingHoursModified: () => async (timeSlots: string[]) =>
    {
      const { openingHoursModified } = await import('~/stores-datalayer/branch.datalayer');
      return await openingHoursModified(timeSlots);
    },

  },
  actions: {
    // --------------------- SERVICE INITIALIZER ------------------------ //
    initialize() {
      const runtimeConfig = useRuntimeConfig();

      this.ecomService = new EcomService();

      this.woosmapService = new WoosmapService(
        runtimeConfig.public.woosmapApiKey,
        runtimeConfig.public.woosmapBaseApiUrl
      );
    },

    async delayedAutoCompleteSearch(countries = ["nl"], delayTime = 500,)
    {
      const { delayedAutoCompleteSearch } = await import('~/stores-datalayer/branch.datalayer');
      return await delayedAutoCompleteSearch(this, countries, delayTime);
    },

    //--------------- Common helper function to fetch results from ECOM ../branches/nearest --------------------//
    async getNearestBranches(
      request: NearestBranchRequest,
      options?: {
        isLocalityBased?: boolean;
        isPostcodeBased?: boolean;
      }
    )
    {
      const { getNearestBranches } = await import('~/stores-datalayer/branch.datalayer');
      return await getNearestBranches(this,request, options);
    },

    //--------------- Resetter method to reset all error and success states during a request --------------------//
    resetStatuses() {
      this.noNearestBranchAvailable = false;
      this.noSuggestionsAvailable = false;
    },

    //--------------- Set debug values from route query or other source --------------------//
    async setDebugValues(
      latitude: string,
      longitude: string,
      query?: string,
      radius?: string
    ) {
      const { setDebugValues } = await import('~/stores-datalayer/branch.datalayer');
      return await setDebugValues(this,latitude, longitude, query, radius);
    },
    //--------------- Common helper function to connect to location service --------------------//
    async useCurrentLocation()
    {
      const { useCurrentLocation } = await import('~/stores-datalayer/branch.datalayer');
      return await useCurrentLocation(this);
    },
    //------------------- Get locality details from Woosmap ---------------------------//
    async getLocalityDetails(public_id: string)
    {
      const { getLocalityDetails } = await import('~/stores-datalayer/branch.datalayer');
      return await getLocalityDetails(this, public_id);
    },

    //------------------- Selecting one suggestion ---------------------------//
    async onSelectLocality()
    {
      const { onSelectLocality } = await import('~/stores-datalayer/branch.datalayer');
      return await onSelectLocality(this);
    },

    //------------------- Fetch branch based on current location------------------//
    async getNearestBranchByCurrentLocation() {
      const { getNearestBranchByCurrentLocation } = await import('~/stores-datalayer/branch.datalayer');
      return await getNearestBranchByCurrentLocation(this);
    },

    //-------------------- Nearest branches based on postcode (on Enter press) -------------------//
    async getPostcodeBasedResults()
    {
      const { getPostcodeBasedResults } = await import('~/stores-datalayer/branch.datalayer');
      return await getPostcodeBasedResults(this);
    },

    /* Get all branches info from ecom */
    async getAllBranches(nuxtApp = null,runtimeConfig = null)
    {
      this.resetStatuses();
      try {
          const response = await new EcomService().fetchAllBranches(nuxtApp,runtimeConfig);

          if (!response || !response.data || !response.data.length) {
            this.allBranches = [];
            throw new Error("ecom all branches fetch error");
          }

          this.allBranches = response.data;

          // Sort branches alphabetically by name
          const sortedBranches = response.data
              .slice()
              .sort((a: Branch, b: Branch) => {
                  return a.name.localeCompare(b.name);
              });

              this.allBranchNames = sortedBranches.map(
              (branch: Branch) => branch.name
          );

          // Categorize branches by first letter //
          this.categorizedAllBranches = sortedBranches.reduce(
              (acc: Record<string, Branch[]>, branch: Branch) => {
                  const firstLetter = branch.name.charAt(0).toUpperCase();
                  if (!acc[firstLetter]) {
                      acc[firstLetter] = [];
                  }
                  acc[firstLetter].push(branch);
                  return acc;
              },
              {}
          );
      } catch (err) {
        const { $customClientWarningHandler } = useNuxtApp();
        const errorMeta = {err, branchStoreWarn: true}
        $customClientWarningHandler(
            'high',
            'Branch Store Error - trying to get all branches using getAllBranches()',
            errorMeta
        );  
        this.allBranches = [];
        return err;
      }
    },

    /* Get branch details */
    async getBranchById(branchId: string)
    {
      const { getBranchById } = await import('~/stores-datalayer/branch.datalayer');
      return await getBranchById(this, branchId);
    },

    async extractBranchDetail(branchSlug: string,nuxtApp = null,runtimeConfig = null) {
      try {
          // Calling this composable in a class method causing 500 
          const response: { data: Branch[] } = await useAjaxEcom("/branches", {
            show_notifications: false,
            show_loading_indicator: false,
          },nuxtApp,runtimeConfig);
          if (!response || !response.data) {
              throw new EcomError("ecom branch details page all branch fetch error");
          }
          const branch = response.data.find(branch => toRoutingSlug(branch.name) === branchSlug);
          if(branch) this.targetBranch = branch;
          return branch;
      } catch (err) {
        const { $customClientErrorHandler } = useNuxtApp();
        const errorMeta = { branchSlug, branchDatalayerError: true };
        $customClientErrorHandler(
            err,
            'critical',
            'Branch Get Error - trying to get extract a branch detail using extractBranchDetail()',
            errorMeta
        );  
        this.targetBranch = null;
        return null;
      }
    },

    async onSaveBranch(branch: Branch, saveAsDefault = false) {
     try{ 
      this.resetStatuses();
      this.branchSelectorModalVisible = false;
      this.currentBranchChoice = branch;

      logEvent({
        options: {
          eventAction: "click",
          eventProperties: {
            branch_name: branch.name,
          },
          eventInteraction: true,
          eventLabel: `User Selected: ${branch.name} branch`,
          eventName: "account_select_branche",
        },
      });

      if (saveAsDefault) {
        const authStore = useAuthStore();
        this.defaultBranch = branch;
        if (authStore.is_authenticated)
          EcomCustomerService.setDefaultBranch(
            (authStore.user as any)?.id,
            this.defaultBranch.id
          );
      } else {
        this.currentSavedBranch = branch;
      }
     
      this.lastSavedBranch = branch as Branch;
      const message = `${useTranslation('selectedTrolleyItem' , 'Je afhaallocatie is gewijzigd naar')} ${branch.name}`;

      setTimeout(() => {
        toast(message, {
          autoClose: true,
          type: "success",
        })
      }, 600)

      this.isDefaultBranchSelected = false
      // GTM Logger for branch change
      logEvent({
        options: {
          eventAction: "click",
          eventProperties: {
            selectedBranch: {
              id: this.lastSavedBranch?.id,
              name: this.lastSavedBranch?.name,
              addressId: this.lastSavedBranch?.address_id
            },
            defaultBranch: {
              id: this.defaultBranch?.id, name: this.defaultBranch?.name, addressId: this.defaultBranch?.address_id
            }
          },
          eventInteraction: true,
          eventLabel: `User changed his branch to ${this.lastSavedBranch.id}`,
          eventName: "header_selected_branche",
        },
      })
    }catch(e){
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {branch: branch, saveAsDefault:saveAsDefault, branchStoreError: true }
        $customClientErrorHandler(
            e,
            'critical',
            'Branch Store Error - trying to onSaveBranch() using onSaveBranch()',
            errorMeta
        );  
        return e;
      }
    },

    //-------------------- Filter all branches based on radius -------------------//
    async filterByRadius(radius: number, unit = "km")
    {
      const { filterByRadius } = await import('~/stores-datalayer/branch.datalayer');
      return await filterByRadius(this, radius, unit);
    },

    //-------------------- Show Route >>> Redirect to google maps -------------------//
    async onShowRoute(branch: Branch, showToast = true)
    {
      const { onShowRoute } = await import('~/stores-datalayer/branch.datalayer');
      return await onShowRoute(this, branch, showToast);
    },

    // ------ Upon signin, set default branch data if found - setter method
    setBranchStates(branch: Branch){
      this.currentBranchChoice = branch;
      this.currentSavedBranch = branch;
      this.lastSavedBranch = branch;
    },

    // ------ Upon signout, reset selected branch data
    resetPersistingBranches() {
      this.currentBranchChoice = null;
      this.currentSavedBranch = null;
      this.lastSavedBranch = null;
      this.defaultBranch = null;
    },

    async listenToBranchSet(): Promise<boolean> {
      if (this.is_branch_set) return true;
      this.branchSelectorModalVisible = true;
      return new Promise((resolve) => {
        const unwatch = watch(
            () => this.is_branch_set,
            (isSet) => {
              if (isSet) {
                unwatch(); // Stop watching after branch is set
                resolve(true);
              } else {
                resolve(false);
              }
            }
        );
      });
    },
  },
});

/* Pinia hot reload */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useBranchStore, import.meta.hot));
}
